import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faFilterCircleXmark,
    faMap,
} from "@fortawesome/sharp-light-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useDebounce, useDocumentTitle } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useMemo, useState } from "react";
import type { Equipment } from "../../../apiClient/generated";
import { useInfrastructureApiClient } from "../../../hooks";
import { ROUTES } from "../../../routes";
import { SecondaryButton } from "../../../ui/Buttons";
import { CoordinatesField } from "../../../ui/GenericFields";
import { SearchInput } from "../../../ui/Inputs";
import { formatDate } from "../../../utils/dates";
import { DataTableV3, RowActionButtons } from "../../DataTable/DataTableV3";
import { tableStateFamily } from "../../DataTable/state";
import { LocationFilterV2 } from "../../DataTable/TableFilters";

const columnHelper = createColumnHelper<Equipment>();

export const EquipmentApiDataTable = () => {
    useDocumentTitle("Equipment - AERSHED");
    const apiClient = useInfrastructureApiClient();
    const [filterSearch, setFilterSearch] = useState("");
    const debouncedSearch = useDebounce(filterSearch, 600);

    // Table state
    const tableAtom = useMemo(
        () =>
            tableStateFamily({
                tableId: "equipment",
                initialState: {
                    columnVisibility: {
                        infraStatus: false,
                        dateOfInstallation: false,
                    },
                },
            }),
        [],
    );
    useAtom(tableAtom);
    const resetTableState = useResetAtom(tableAtom);

    const fetchFunction = async (props: any) => {
        return await apiClient.infrastructureEquipmentList(props);
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor("siteName", {
                id: "site",
                header: "Site",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor("location", {
                id: "location",
                header: () => "Location",
                cell: (info) => {
                    const value = info.getValue();
                    if (value) {
                        return (
                            <CoordinatesField
                                coordinates={value.coordinates}
                                allowCopy={true}
                                mapLinkParams={{
                                    siteId: info.row.original.site,
                                    lat: value.coordinates[1].toString(),
                                    lon: value.coordinates[0].toString(),
                                }}
                            />
                        );
                    }
                    return "-";
                },
                size: 160,
                meta: {
                    customFilterWidget: LocationFilterV2,
                },
                enableSorting: false,
            }),
            columnHelper.accessor("manufacturerMake", {
                id: "manufacturerMake",
                cell: (info) => (
                    <div className="overflow-hidden truncate">
                        {info.getValue()}
                    </div>
                ),
                header: "Manufacturer",
            }),
            columnHelper.accessor("manufacturerModel", {
                id: "manufacturerModel",
                cell: (info) => info.getValue(),
                header: "Model",
            }),
            columnHelper.accessor("operatorStatus", {
                id: "operatorStatus",
                cell: (info) => info.getValue(),
                header: "Status",
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor("dateOfInstallation", {
                id: "dateOfInstallation",
                cell: (info) => {
                    const value = info.getValue();
                    return formatDate(value);
                },
                header: "Installation Date",
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor("material", {
                id: "material",
                cell: (info) => info.getValue(),
                header: "Material",
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor("fuel", {
                id: "fuel",
                cell: (info) => info.getValue(),
                header: "Fuel",
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor(
                (row) => ({
                    operatorUniqueId: row.operatorUniqueId,
                    otherOperatorIds: row.otherOperatorIds,
                }),
                {
                    id: "operatorUniqueIds",
                    header: () => "Operator IDs",
                    cell: (info) => {
                        const value = info.getValue();
                        let opIds = "";
                        if (value.operatorUniqueId.trim()) {
                            opIds = value.operatorUniqueId;
                        }
                        if (value.otherOperatorIds.trim()) {
                            opIds += value.otherOperatorIds
                                .split("¦")
                                .join(", ");
                        }
                        if (opIds.trim()) {
                            return opIds;
                        }
                        return "-";
                    },
                    enableSorting: false,
                    enableColumnFilter: false,
                },
            ),

            columnHelper.display({
                id: "actions",
                header: "",
                cell: (props) => {
                    const actions = [
                        {
                            fn: () => {
                                window.open(
                                    `${ROUTES.INFRASTRUCTURE_SITE}?site=${props.row.original.site}`,
                                    "_blank",
                                );
                            },
                            icon: (
                                <FontAwesomeIcon
                                    icon={faBuilding}
                                    className="w-3"
                                />
                            ),
                            tooltip: "View site page",
                        },
                        {
                            fn: () => {
                                window.open(
                                    `${ROUTES.MAP}?lat=${props.row.original.location.coordinates[1]}&lon=${props.row.original.location.coordinates[0]}`,
                                    "_blank",
                                );
                            },
                            icon: (
                                <FontAwesomeIcon icon={faMap} className="w-3" />
                            ),
                            tooltip: "Open map",
                        },
                    ];

                    return <RowActionButtons actions={actions} />;
                },
            }),
        ],
        [],
    );

    return (
        <div className="flex-1 flex flex-col">
            {/* Search bar */}
            <div className="py-3 px-4 flex items-center text-sm justify-between border-b">
                <SearchInput
                    placeholder="Search by name, type, facility..."
                    value={filterSearch}
                    onChange={(newValue) => setFilterSearch(newValue)}
                    className="w-96"
                />
                <div className="flex-grow" />
                <div className="flex gap-2 items-center">
                    <SecondaryButton variant="sm" onClick={resetTableState}>
                        <FontAwesomeIcon
                            icon={faFilterCircleXmark}
                            className="w-4"
                        />
                        Clear filters
                    </SecondaryButton>
                </div>
            </div>

            {/* Data table */}
            <DataTableV3<Equipment>
                dataName="equipment"
                columns={columns}
                extraFilters={{
                    search: debouncedSearch,
                }}
                fetchFunction={fetchFunction}
                sortable={true}
                filterable={true}
            />
        </div>
    );
};
